import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import toast from 'react-hot-toast';

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMethodology, setSelectedMethodology] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [teamPreferences, setTeamPreferences] = useState({
    notifications: true,
    darkMode: false,
    weeklyReports: true
  });
  
  const { user, updateUser } = useAuth();
  const { setMethodology } = useProject();
  const navigate = useNavigate();

  const methodologies = [
    {
      id: 'scrum',
      name: 'Scrum',
      description: 'Metodología ágil con sprints cortos y roles definidos',
      icon: '🏃‍♂️',
      features: ['Sprints de 1-4 semanas', 'Daily standups', 'Sprint planning', 'Retrospectivas', 'Product backlog'],
      color: 'blue'
    },
    {
      id: 'kanban',
      name: 'Kanban',
      description: 'Flujo continuo con límites de trabajo en progreso',
      icon: '📋',
      features: ['Flujo continuo', 'Límites WIP', 'Visualización clara', 'Mejora continua', 'Flexibilidad'],
      color: 'green'
    },
    {
      id: 'lean',
      name: 'Lean',
      description: 'Eliminación de desperdicios y optimización de valor',
      icon: '⚡',
      features: ['Eliminación de desperdicios', 'Valor del cliente', 'Flujo optimizado', 'Mejora continua', 'Simplicidad'],
      color: 'purple'
    },
    {
      id: 'agile',
      name: 'Agile',
      description: 'Enfoque iterativo e incremental para desarrollo',
      icon: '🔄',
      features: ['Iteraciones cortas', 'Colaboración', 'Adaptabilidad', 'Entrega frecuente', 'Feedback continuo'],
      color: 'orange'
    }
  ];

  const roles = [
    {
      id: 'admin',
      name: 'Administrador',
      description: 'Control total del proyecto y configuración',
      icon: '👑',
      permissions: ['Gestión completa', 'Configuración', 'Reportes', 'Usuarios']
    },
    {
      id: 'project-manager',
      name: 'Project Manager',
      description: 'Gestión de proyectos y coordinación de equipos',
      icon: '📊',
      permissions: ['Gestión de proyectos', 'Planificación', 'Reportes', 'Coordinación']
    },
    {
      id: 'developer',
      name: 'Desarrollador',
      description: 'Desarrollo y implementación de funcionalidades',
      icon: '💻',
      permissions: ['Desarrollo', 'Tareas técnicas', 'Code review', 'Testing']
    },
    {
      id: 'qa',
      name: 'QA/Tester',
      description: 'Aseguramiento de calidad y testing',
      icon: '🔍',
      permissions: ['Testing', 'Reportes de bugs', 'Validación', 'Calidad']
    },
    {
      id: 'designer',
      name: 'Diseñador',
      description: 'Diseño de interfaces y experiencia de usuario',
      icon: '🎨',
      permissions: ['Diseño', 'Prototipos', 'UI/UX', 'Assets']
    }
  ];

  const steps = [
    {
      title: '¡Bienvenido a TeamiFlow!',
      subtitle: 'Configuremos tu experiencia de trabajo',
      content: 'welcome'
    },
    {
      title: 'Elige tu metodología',
      subtitle: 'Selecciona el enfoque que mejor se adapte a tu equipo',
      content: 'methodology'
    },
    {
      title: 'Define tu rol',
      subtitle: '¿Cuál es tu función principal en el equipo?',
      content: 'role'
    },
    {
      title: 'Personaliza tu experiencia',
      subtitle: 'Ajusta las preferencias según tus necesidades',
      content: 'preferences'
    },
    {
      title: '¡Todo listo!',
      subtitle: 'Ya puedes comenzar a gestionar tus proyectos',
      content: 'complete'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      // Actualizar usuario con las preferencias seleccionadas
      await updateUser({
        methodology: selectedMethodology,
        role: selectedRole,
        preferences: teamPreferences,
        onboardingCompleted: true
      });

      // Actualizar metodología en el contexto de proyecto
      setMethodology(selectedMethodology);

      toast.success('¡Configuración completada!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al completar la configuración');
    }
  };

  const renderContent = () => {
    switch (steps[currentStep].content) {
      case 'welcome':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <div className="w-24 h-24 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto">
              <span className="text-4xl">🚀</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                ¡Hola, {user?.name}! 👋
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Estamos emocionados de tenerte aquí. Te ayudaremos a configurar TeamiFlow 
                para que se adapte perfectamente a tu forma de trabajar.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-2xl">⚡</span>
                <span className="text-sm font-medium">Configuración rápida</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-2xl">🎯</span>
                <span className="text-sm font-medium">Personalizado para ti</span>
              </div>
            </div>
          </motion.div>
        );

      case 'methodology':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {methodologies.map((method) => (
                <motion.div
                  key={method.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`card cursor-pointer transition-all duration-200 ${
                    selectedMethodology === method.id
                      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:shadow-medium'
                  }`}
                  onClick={() => setSelectedMethodology(method.id)}
                >
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="text-3xl">{method.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {method.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {method.description}
                        </p>
                        <div className="space-y-1">
                          {method.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <CheckIcon className="h-4 w-4 text-success-500" />
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {selectedMethodology === method.id && (
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );

      case 'role':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map((role) => (
                <motion.div
                  key={role.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`card cursor-pointer transition-all duration-200 ${
                    selectedRole === role.id
                      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:shadow-medium'
                  }`}
                  onClick={() => setSelectedRole(role.id)}
                >
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="text-3xl">{role.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {role.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {role.description}
                        </p>
                        <div className="space-y-1">
                          {role.permissions.map((permission, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {permission}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {selectedRole === role.id && (
                        <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );

      case 'preferences':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Preferencias de notificaciones
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Notificaciones push
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Recibe notificaciones sobre cambios importantes
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={teamPreferences.notifications}
                      onChange={(e) => setTeamPreferences({
                        ...teamPreferences,
                        notifications: e.target.checked
                      })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </label>
                  
                  <label className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Modo oscuro
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Usar tema oscuro por defecto
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={teamPreferences.darkMode}
                      onChange={(e) => setTeamPreferences({
                        ...teamPreferences,
                        darkMode: e.target.checked
                      })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </label>
                  
                  <label className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Reportes semanales
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Recibe un resumen semanal de progreso
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={teamPreferences.weeklyReports}
                      onChange={(e) => setTeamPreferences({
                        ...teamPreferences,
                        weeklyReports: e.target.checked
                      })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'complete':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-6"
          >
            <div className="w-24 h-24 bg-gradient-to-r from-success-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto">
              <CheckIcon className="h-12 w-12 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                ¡Configuración completada! 🎉
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Tu espacio de trabajo está listo. Comienza a crear proyectos y 
                colaborar con tu equipo de manera eficiente.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-w-md mx-auto">
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div>✅ Metodología: {methodologies.find(m => m.id === selectedMethodology)?.name}</div>
                <div>✅ Rol: {roles.find(r => r.id === selectedRole)?.name}</div>
                <div>✅ Preferencias configuradas</div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8"
        >
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Paso {currentStep + 1} de {steps.length}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentStep + 1) / steps.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {steps[currentStep].title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {steps[currentStep].subtitle}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {renderContent()}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span>Anterior</span>
            </button>

            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep
                      ? 'bg-primary-600'
                      : index < currentStep
                      ? 'bg-primary-300'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !selectedMethodology) ||
                (currentStep === 2 && !selectedRole)
              }
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>
                {currentStep === steps.length - 1 ? 'Comenzar' : 'Siguiente'}
              </span>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Onboarding;

